import { defaultFilter } from 'cmdk';

import { toNfc } from '@heidi/utils';

import type { CommandProps } from './command.types';

// cmdk's scorer recurses once per search character, so a pasted query thousands
// of characters long overflows the stack and takes the page down with it. Fuzzy
// matching means nothing at that length; a substring match is what the user
// meant.
const MAX_FUZZY_SEARCH_LENGTH = 100;

// cmdk's scorer lowercases and maps each whitespace or hyphen to a space. This
// also collapses runs of them and trims: cmdk trims item values but not the
// query, and pasted text usually carries trailing whitespace.
const normalizeForSubstring = (text: string) =>
  text
    .toLowerCase()
    .replace(/[\s-]+/g, ' ')
    .trim();

// cmdk scores one item at a time with the same query, so each scorer normalizes
// a long query once and holds it per instance, like the filters below.
export const createBoundedScorer = () => {
  let lastLongSearch = '';
  let lastLongSearchNormalized = '';
  return (value: string, search: string, keywords?: string[]): number => {
    if (search.length <= MAX_FUZZY_SEARCH_LENGTH) {
      return defaultFilter(value, search, keywords);
    }
    if (search !== lastLongSearch) {
      lastLongSearch = search;
      lastLongSearchNormalized = normalizeForSubstring(search);
    }
    const haystack = keywords?.length ? `${value} ${keywords.join(' ')}` : value;
    return normalizeForSubstring(haystack).includes(lastLongSearchNormalized) ? 1 : 0;
  };
};

// macOS Korean IME emits NFD (decomposed jamo) while labels in code/data are
// typically NFC (precomposed). cmdk's scorer is a raw codepoint scan, so a
// mismatch scores 0 despite rendering identically — same root cause Combobox
// already covers. Callers that pass their own `filter` keep it.
// Search is NFC-normalized once per query (cmdk invokes this per item).
export const createNfcAwareFilter = (): NonNullable<CommandProps['filter']> => {
  const score = createBoundedScorer();
  let lastSearch = '';
  let lastSearchNfc = '';
  return (value, search, keywords) => {
    if (search !== lastSearch) {
      lastSearch = search;
      lastSearchNfc = toNfc(search);
    }
    return score(toNfc(value), lastSearchNfc, keywords?.map(toNfc));
  };
};

// For lists whose `value` is an opaque id (a UUID) rather than a display
// string: cmdk scores `value + ' ' + keywords` as one haystack, so the id
// becomes searchable and any hex letter fuzzy-matches every option whose id
// contains it. Scores the keywords alone, keeping `value` identity-only.
// NFC-normalized on both sides like `createNfcAwareFilter`.
export const createKeywordsOnlyFilter = (): NonNullable<CommandProps['filter']> => {
  const score = createBoundedScorer();
  let lastSearch = '';
  let lastSearchNfc = '';
  return (_value, search, keywords) => {
    if (search !== lastSearch) {
      lastSearch = search;
      lastSearchNfc = toNfc(search);
    }
    return score(toNfc(keywords?.join(' ') ?? ''), lastSearchNfc);
  };
};
